import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import axios, { CancelTokenSource } from "axios";
import { KButton, KIcon } from "@kyburz/kdesign-system";

import KInfiniteTable from "../../components/table/KInfiniteTable";
import useSetState from "../../hooks/useTypedSetState";
import { BillingInvoiceInterface } from "../../interfaces/BillingInvoiceInterface";
import {
  deleteBillingInvoice,
  getBillingInvoices,
  regenerateBillingInvoice,
} from "../../apiCalls/admin/billing_invoices";
import { BillingInvoicesColumns } from "./typings";
import { DATE_FORMAT, formatDate } from "../../utils/timeUtils";
import TableDropdown from "../../components/TableDropdown";
import NewInternalInvoice from "./components/NewInvoice";
import BaseLayout from "../../components/layouts/BaseLayout";

const useStyles = makeStyles(({ breakpoints }) => ({
  tableContainer: {
    height: "calc(100% - 100px)",
  },
  ctaContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 16,
    [breakpoints.down("sm")]: {
      margin: 5,
      marginTop: 5,
    },
  },
  downloadButton: {
    fontSize: 32,
    minWidth: "100%",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&:disabled": {
      backgroundColor: "transparent",
    },
  },
  downloadButtonGenerating: {
    backgroundColor: "transparent",
    animation: "$rotateButton 2s infinite linear",
  },
  "@keyframes rotateButton": {
    "0%": {
      "-webkit-transform": "rotate(0deg)",
    },
    "100%": {
      "-webkit-transform": "rotate(360deg)",
    },
  },
}));

const defaultSortingRules = {
  id: "created",
  order: "desc",
};

const InternalBilling = () => {
  const { CancelToken } = axios;
  const classes = useStyles();

  const [
    {
      items,
      cancelTokenSource,
      hasNextPage,
      isNextPageLoading,
      pagesLoaded,
      order,
      filterBy,
      reload,
      loading,
      regeneratingInvoicesIds,
      isCreateInvoiceModalOpen,
    },
    setState,
  ] = useSetState({
    items: [] as BillingInvoiceInterface[],
    cancelTokenSource: null as CancelTokenSource | null,
    hasNextPage: true,
    isNextPageLoading: false,
    pagesLoaded: 0,
    order: defaultSortingRules,
    filterBy: "",
    reload: false,
    loading: false,
    regeneratingInvoicesIds: [] as string[],
    isCreateInvoiceModalOpen: false,
  });

  const fetchInvoices = (
    page: number,
    refresh: boolean = true,
    filter: string = "",
  ): void => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    const sourceToken = source.token;
    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    const previousArray = refresh ? [] : items;
    getBillingInvoices(page, filter, sourceToken, order.id, order.order)
      .then((res) => {
        if (!res || !res?.data) return;
        const {
          data: { data },
          hasNextPage: resHasNextPage,
        } = res;
        setState({
          items: [...previousArray, ...data],
          isNextPageLoading: false,
          pagesLoaded: page,
          loading: false,
          hasNextPage: resHasNextPage,
        });
      })
      .finally(() => {
        setState({
          isNextPageLoading: false,
        });
      });
  };

  useEffect(() => fetchInvoices(1, true, filterBy), [filterBy, order]);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      fetchInvoices(1, true, filterBy);
    }
  }, [reload]);

  const loadNextPage = () => fetchInvoices(pagesLoaded + 1, false, filterBy);

  const filterTable = (values: { id: string; value: string }[]): void => {
    const parsedFilter: string = values
      .map((x) => `${x.id}:regex:${x.value}`)
      .join(";");
    setState({ filterBy: parsedFilter });
  };

  const sortTable = (data) => {
    const rules = data[0];
    if (rules) {
      setState({
        order: {
          id: rules.id,
          order: rules.order || rules.desc ? "desc" : "asc",
        },
      });
    }
  };

  const handleDownload = (url: string) => {
    window.location.href = url;
  };

  const handleDelete = (invoiceId: string) => {
    deleteBillingInvoice(invoiceId).then(() => setState({ reload: true }));
  };

  const addRegenerateState = (invoiceId: string) => {
    setState({
      regeneratingInvoicesIds: [...regeneratingInvoicesIds, invoiceId],
    });
  };

  const removeRegenerateState = (invoiceId: string) => {
    const regeneratingInvoicesIdsNew = [...regeneratingInvoicesIds];
    const index = regeneratingInvoicesIdsNew.indexOf(invoiceId);
    regeneratingInvoicesIdsNew.splice(index, 1);
    setState({
      regeneratingInvoicesIds: regeneratingInvoicesIdsNew,
      reload: true,
    });
  };

  const handleRegenerate = (invoiceId: string) => {
    addRegenerateState(invoiceId);
    regenerateBillingInvoice(invoiceId).then(() => {
      removeRegenerateState(invoiceId);
    });
  };

  const renderDownloadButton = (url: string, isGenerating: boolean) => {
    return (
      <KButton
        theme="primary"
        onClick={() => handleDownload(url)}
        className={classes.downloadButton}
        disabled={isGenerating}
      >
        <div className={isGenerating ? classes.downloadButtonGenerating : ""}>
          💵
        </div>
      </KButton>
    );
  };

  const renderCompany = (invoice: BillingInvoiceInterface) => {
    let res = invoice?.company?.name;
    if (invoice?.company?.child_companies?.length) {
      const childCompanies = invoice.company.child_companies
        .map((company) => company.name)
        .join(", ");
      res += ` (${childCompanies})`;
    }
    return res || "-";
  };

  const renderTableActions = (row: BillingInvoiceInterface) => (
    <TableDropdown
      menuItems={[
        {
          icon: "refresh-cw",
          title: "Regenerate",
          action: () => handleRegenerate(row._id),
        },
        {
          icon: "trash-2",
          title: "Delete",
          action: () => handleDelete(row._id),
        },
      ]}
    />
  );

  const columns = React.useMemo(
    () => [
      {
        id: BillingInvoicesColumns.created,
        Header: "Created",
        accessor: (invoice: BillingInvoiceInterface) =>
          formatDate(invoice.created, DATE_FORMAT),
        defaultCanSort: true,
        width: 100,
      },
      {
        id: BillingInvoicesColumns.company,
        Header: "Shortname",
        accessor: "company_id",
        defaultCanSort: true,
      },
      {
        id: BillingInvoicesColumns.companies,
        Header: "Companies",
        accessor: (invoice: BillingInvoiceInterface) => renderCompany(invoice),
        defaultCanSort: false,
        disableFilters: true,
      },
      {
        id: BillingInvoicesColumns.from,
        Header: "From",
        accessor: (invoice: BillingInvoiceInterface) =>
          formatDate(invoice.date_from, DATE_FORMAT),
        defaultCanSort: true,
        width: 100,
      },
      {
        id: BillingInvoicesColumns.to,
        Header: "To",
        accessor: (invoice: BillingInvoiceInterface) =>
          formatDate(invoice.date_to, DATE_FORMAT),
        defaultCanSort: true,
        width: 100,
      },
      {
        id: BillingInvoicesColumns.total,
        Header: "Total",
        accessor: (invoice: BillingInvoiceInterface) =>
          `${invoice.total.toFixed(2)} ${invoice?.company?.currency || ""}`,
        defaultCanSort: false,
        disableFilters: true,
        width: 100,
      },
      {
        id: BillingInvoicesColumns.xlsx,
        Header: "XLSX",
        Cell: (row) =>
          renderDownloadButton(
            row.row.original.file_urls.xlsx,
            regeneratingInvoicesIds.indexOf(row.row.original._id) > -1,
          ),
        defaultCanSort: false,
        disableFilters: true,
        width: 50,
      },
      {
        id: BillingInvoicesColumns.pdf,
        Header: "PDF",
        Cell: (row) =>
          renderDownloadButton(
            row.row.original.file_urls.pdf,
            regeneratingInvoicesIds.indexOf(row.row.original._id) > -1,
          ),
        defaultCanSort: false,
        disableFilters: true,
        width: 50,
      },
      {
        id: BillingInvoicesColumns.actions,
        Header: "",
        accessor: "",
        width: 50,
        Cell: (row) => renderTableActions(row.row.original),
        disableFilters: true,
        sticky: "right",
      },
    ],
    [regeneratingInvoicesIds],
  );

  const toggleCreateInvoiceModal = (withReload: boolean) => {
    setState({
      isCreateInvoiceModalOpen: !isCreateInvoiceModalOpen,
      reload: withReload,
    });
  };

  const reloadInvoices = () => setState({ reload: true });

  return (
    <BaseLayout title="Billing">
      <div className={classes.ctaContainer}>
        <KButton icon={<KIcon icon="refresh-cw" />} onClick={reloadInvoices}>
          Reload
        </KButton>
        <KButton
          icon={<KIcon icon="plus" />}
          onClick={() => toggleCreateInvoiceModal(false)}
        >
          Create Invoice
        </KButton>
      </div>
      <NewInternalInvoice
        open={isCreateInvoiceModalOpen}
        handleClose={toggleCreateInvoiceModal}
      />
      <div className={classes.tableContainer}>
        <KInfiniteTable
          data={items}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          manualSortBy={true}
          initialSortBy={[defaultSortingRules]}
          sortTable={sortTable}
          hasFilter
          manualFilters
          filterTable={filterTable}
          loading={loading}
        />
      </div>
    </BaseLayout>
  );
};

export default InternalBilling;
